import {
	CardType,
	ControlType,
	type FilterCardConfig,
} from '@framework/analytics/models/card-model';
import {
	type CardList,
	DROPDOWN_FILTER_LAYOUT_H,
	DROPDOWN_FILTER_LAYOUT_MIN_H,
} from '@framework/analytics/models/layout.model';

export interface ResolveFilterControlTypeInput {
	layout?: Pick<CardList, 'controlType' | 'cardId'>;
	config?: Pick<FilterCardConfig, 'controlType' | 'cardId'>;
}

/**
 * Resolves filter UI from persisted {@link FilterCardConfig.controlType} and layout
 * {@link CardList.controlType} only. Runtime WebSocket payload must not change control type.
 *
 * @throws When neither config nor layout provides `controlType`.
 */
export function resolveFilterControlType(
	input: ResolveFilterControlTypeInput
): ControlType {
	const { layout, config } = input;

	if (config?.controlType) {
		return config.controlType;
	}

	if (layout?.controlType) {
		return layout.controlType;
	}

	const cardId = config?.cardId ?? layout?.cardId;
	const cardPart = cardId ? ` (cardId=${cardId})` : '';
	throw new Error(
		`Filter card controlType is required${cardPart}. Set controlType on FilterCardConfig or CardList.`
	);
}

export function isDropdownFilterControlType(
	controlType: ControlType | null
): boolean {
	return controlType === ControlType.Dropdown;
}

export function isSearchFilterControlType(
	controlType: ControlType | null
): boolean {
	return controlType === ControlType.Search;
}

/** Dropdown, search and date-range filters are single-control tiles on the report grid. */
export function isCompactFilterControlType(
	controlType: ControlType | null | undefined
): boolean {
	return (
		controlType === ControlType.Dropdown ||
		controlType === ControlType.Search ||
		controlType === ControlType.DateRange
	);
}

/** Fixed GridStack row span for compact filter tiles (vertical resize disabled). */
export function compactFilterLayoutRowHeight(
	controlType: ControlType | null | undefined
): number | null {
	return isCompactFilterControlType(controlType)
		? DROPDOWN_FILTER_LAYOUT_H
		: null;
}

/** GridStack `minH` for compact filter tiles. */
export function compactFilterLayoutMinRowHeight(
	controlType: ControlType | null | undefined
): number | null {
	return isCompactFilterControlType(controlType)
		? DROPDOWN_FILTER_LAYOUT_MIN_H
		: null;
}

/** Normalizes persisted layout rows for compact filter cards. */
export function normalizeCompactFilterLayoutCard(
	card: CardList,
	controlType?: ControlType | null
): CardList {
	const resolved = controlType ?? card.controlType;
	const h = compactFilterLayoutRowHeight(resolved);
	const minH = compactFilterLayoutMinRowHeight(resolved);
	if (card.cardType !== CardType.Filter || h === null || minH === null) {
		return card;
	}

	return {
		...card,
		controlType: resolved ?? undefined,
		h,
		minH,
	};
}
